import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Merchant,Card, DisputeTransactionType,LinkedAccount, TransactionDispute } from "model";

/**
* @class Transaction
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.transactions[0];
* const transaction = new transaction(record);
* @property {NUMBER} _id - number to reference transaction
* @property {Card} _card - instance of card used for transaction
* @property {Merchant} _merchant - instance of merchant used for transaction
* @property {bcmsCurrency} _amount - the amount of transaction
* @property {STRING} _type - the type of transaction
* @property {STRING} _purchaseLocation - the purchase location for the transaction
* @property {bcmsDate} _purchaseDate- number representing offset for the purchase date
* @property {STRING} _timeOfPurchase - static time of purchase
* @property {STRING} _purchaseTimeZone - time zone of where purchase was made
* @property {STRING} _status - status of the transaction
* @property {STRING} _mapOfPurchase - name of map for purchase of the transaction
* @property {STRING} _locationOfPurchase - name of location for purchase
* @property {STRING} _credit - determines if the transaction is credit or not
* @property {LinkedAccount} _account - if transaction is payment the account payment was made from
* @property {BOOLEAN}  _isCanceled - if transaction is canceled
*/

export default class Transaction extends BCMSRecord {
  _id 
  _card 
  _merchant
  _amount
  _type
  _purchaseLocation
  _purchaseDate
  _timeOfPurchase 
  _purchaseTimeZone
  _status
  _mapOfPurchase
  _locationOfPurchase
  _credit
  _disputeTransaction
  _transactionDispute
  _disputeTransactionType
  _account
  _canceledDate
  _isCanceled

  constructor(record) {
    super(record)
  }

  /**
   * @function getByCard
   * @param {STRING} id - The id of the card
   * @returns {ARRAY}
   * @description Get all the transactions for a specified card id and sort by date
   * @memberof Transaction
   */
  static getByCard(id) {
    return Transaction.initAll().filter(transaction => transaction.card.id === id, [])
      .sort((a, b) => {
        if(a.purchaseDate.isBefore(b.purchaseDate)) { return 1; }
        else if(a.purchaseDate.isAfter(b.purchaseDate)) { return -1; }
        else { return 0; }
      });
  }
  /**
   * @function getPaymentsByCard
   * @param {STRING} id - The id of the card
   * @returns {ARRAY}
   * @description Get all the payments for a specified card id and sort by date
   * @memberof Transaction
   */
   static getPaymentsByCard(id) {

    return Transaction.initAll().filter(transaction => transaction.card.id === id && transaction.merchant.name =="Payment", [])
      .sort((a, b) => {
        if(a.purchaseDate.isBefore(b.purchaseDate)) { return 1; }
        else if(a.purchaseDate.isAfter(b.purchaseDate)) { return -1; }
        else { return 0; }
      });
  }
}


  



Transaction.define({
  "recordType": "transactions",
  "recordIDProp": "id",
  "className" : "Transaction",
  "recordProperties": [
     
   
   /** 
    * @name id
    * @instance
    * @property get {STRING} - gets the id of the transaction
    * @property set {STRING} - sets the id of the transaction
    * @memberof transaction 
    */

    BCMSRecordProperty.mapToString("id", "_id"),
   
    /** 
     * @name card
     * @instance
     * @property get {Card} - gets the card of the transaction
     * @property set {Card} - sets the card of the transaction
     * @memberof transaction 
     */

     BCMSRecordProperty.mapToClass("card", "_card",()=>Card),

    /** 
     * @name merchant
     * @instance
     * @property get {Merchant} - gets the merchant of the transaction
     * @property set {Merchant} - sets the merchant of the transaction
     * @memberof transaction 
     */

    BCMSRecordProperty.mapToClass("merchant", "_merchant",()=>Merchant),
    /** 
     * @name amount
     * @instance
     * @property get {bcmsCurrency} - gets the amount of the transaction
     * @property set {bcmsCurrency} - sets the amount of the transaction
     * @memberof transaction 
     */

    BCMSRecordProperty.mapToCurrency("amount", "_amount"),
   
    /** 
     * @name type
     * @instance
     * @property get {STRING} - gets the type of the transaction
     * @property set {STRING} - sets the type of the transaction
     * @memberof transaction 
     */

    BCMSRecordProperty.mapToString("type", "_type"),

   /** 
    * @name purchaseLocation
    * @instance
    * @property get {STRING} - gets the purchaseLocation of the transaction
    * @property set {STRING} - sets the purchaseLocation of the transaction
    * @memberof transaction 
    */

    BCMSRecordProperty.mapToString("purchaseLocation", "_purchaseLocation"),
    
   /** 
    * @name purchaseDate
    * @instance
    * @property get {bcmsDate} - gets the purchaseDate of the transaction
    * @property set {bcmsDate} - sets the purchaseDate of the transaction
    * @memberof transaction 
    */

    BCMSRecordProperty.mapToDate("purchaseDate", "_purchaseDate"),
   
   
    /** 
     * @name timeOfPurchase 
     * @instance
     * @property get {STRING} - gets the timeOfPurchase  of the transaction
     * @property set {STRING} - sets the timeOfPurchase  of the transaction
     * @memberof transaction 
     */

    BCMSRecordProperty.mapToString("timeOfPurchase", "_timeOfPurchase"),
      
   /** 
    * @name purchaseTimeZone
    * @instance
    * @property get {STRING} - gets the purchaseTimeZone of the transaction
    * @property set {STRING} - sets the purchaseTimeZone of the transaction
    * @memberof transaction 
    */   

    BCMSRecordProperty.mapToString("purchaseTimeZone", "_purchaseTimeZone"),
      
    /** 
     * @name status
     * @instance
     * @property get {STRING} - gets the status of the transaction
     * @property set {STRING} - sets the status of the transaction
     * @memberof transaction 
     */

    BCMSRecordProperty.mapToString("status", "_status"),
    /** 
     * @name mapOfPurchase
     * @instance
     * @property get {STRING} - gets the mapOfPurchase of the transaction
     * @property set {STRING} - sets the mapOfPurchase of the transaction
     * @memberof transaction 
     */

     BCMSRecordProperty.mapToString("mapOfPurchase", "_mapOfPurchase"),
     /** 
     * @name locationOfPurchase
     * @instance
     * @property get {STRING} - gets the locationOfPurchase of the transaction
     * @property set {STRING} - sets the locationOfPurchase of the transaction
     * @memberof transaction 
     */

    BCMSRecordProperty.mapToString("locationOfPurchase", "_locationOfPurchase"),
    
    /**
     * @name credit
     * @instance
     * @property get {BOOLEAN} - gets the credit of the transaction
     * @property set {BOOLEAN} - sets the credit of the transaction
     * @memberof transaction
     */
    BCMSRecordProperty.mapToBoolean("credit", "_credit"),

    /**
     * @name disputeTransaction
     * @instance
     * @property get {BOOLEAN} - gets the disputeTransaction value of the transaction
     * @property set {BOOLEAN} - sets the disputeTransaction value of the transaction
     * @memberof transaction
     */
    BCMSRecordProperty.mapToBoolean("disputeTransaction", "_disputeTransaction"),

    /** 
     * @name transactionDispute
     * @instance
     * @property get {Merchant} - gets an instance of a transaction dispute.
     * @property set {Merchant} - sets an instance of a transaction dispute.
     * @memberof transaction 
     */
    BCMSRecordProperty.mapToClass("transactionDispute", "_transactionDispute", () => TransactionDispute),

    /** 
     * @name disputeTransactionType
     * @instance
     * @property get {Merchant} - gets the merchant of the transaction
     * @property set {Merchant} - sets the merchant of the transaction
     * @memberof transaction 
     */
    BCMSRecordProperty.mapToClass("disputeTransactionType", "_disputeTransactionType", () => DisputeTransactionType),

      /** 
     * @name account
     * @instance
     * @property get {LinkedAccount} - gets the LinkedAccount of the transaction
     * @property set {LinkedAccount} - sets the LinkedAccount of the transaction
     * @memberof transaction 
     */
       BCMSRecordProperty.mapToClass("account", "_account", () => LinkedAccount),
        /** 
    * @name canceledDate
    * @instance
    * @property get {bcmsDate} - gets the canceledDate of the transaction
    * @property set {bcmsDate} - sets the canceledDate of the transaction
    * @memberof transaction 
    */

    BCMSRecordProperty.mapToDate("canceledDate", "_canceledDate"),
    /**  
    * @name isCanceled
    * @instance
    * @property get {boolean} - gets the isCanceled of the transaction
    * @property set {boolean} - sets the isCanceled of the transaction
    * @memberof transaction 
    */

    BCMSRecordProperty.mapToBoolean("isCanceled", "_isCanceled"),
  ]
})
